import { FC, ReactNode, Suspense, lazy, useEffect, useState, ComponentType, LazyExoticComponent } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Link as RouterLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Typography, ThemeProvider, CssBaseline, CircularProgress, Link, Stack } from '@mui/material';
import { QueryClient, QueryClientProvider, useIsFetching } from '@tanstack/react-query';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ErrorFallback } from './components/ErrorFallback';
import { AuthProvider } from './contexts/AuthContext';
import theme from './theme';
import { ProtectedRoute } from './components/ProtectedRoute';
import { RoleProtectedRoute } from './components/RoleProtectedRoute';
import { LanguageSwitcher } from './components/LanguageSwitcher';
import { Navigation, hasOwnFooter, isChromelessPath } from './components/Navigation';
import { Landing } from './pages/Landing';
import { Login } from './pages/Login';
import { isSignupEnabled } from './config/features';
import { markRouteChunkLoaded, useRouteChunkLoaded } from './utils/routeReadiness';
import { Unauthorized } from './pages/Unauthorized';

// Admin Pages
import BrandLogo from './components/BrandLogo/BrandLogo';
import FundingBar from './components/FundingBar/FundingBar';
import { CookieConsentBanner } from './components/CookieConsent';
import { openCookieSettings } from './components/CookieConsent/cookieConsentStore';

// Trainer Pages

// Participant Pages

// Mentor Pages

// Manager Pages

// Company Admin Pages

// Programme Pages

// Questionnaire Pages (admin)

// Session Pages (trainer)

// Session Pages (participant/participant)

// Assessment Pages (participant/participant)

// Content Pages (shared — all authenticated roles)

// Notification Pages

// Monitoring Pages (Check-ins & Reports — §3.9, §3.12)

// AI Pages (§4)

// Phase K Pages (§3.14, §3.15, §5 — Payments, GDPR, FAQ, Support)

// Phase L Pages (§3.10 — 360 Assessment)

// Password Reset Pages (public)

// Forced Password Change (authenticated; mustChangePassword accounts)

// Admin operations pages


/**
 * Route-level code splitting: each page is its own chunk, fetched when its route is first visited,
 * so the public pages (landing, login) do not download the whole authenticated application.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function lazyNamed<T extends Record<string, any>, K extends keyof T>(
  load: () => Promise<T>,
  name: K,
): LazyExoticComponent<T[K] extends ComponentType<infer P> ? ComponentType<P> : never> {
  return lazy(() =>
    load().then((m) => {
      markRouteChunkLoaded();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return { default: m[name] as any };
    }),
  );
}

const About = lazyNamed(() => import('./pages/About'), 'About');
const Terms = lazyNamed(() => import('./pages/Terms'), 'Terms');
const PrivacyPolicy = lazyNamed(() => import('./pages/PrivacyPolicy'), 'PrivacyPolicy');
const CookiePolicy = lazyNamed(() => import('./pages/CookiePolicy'), 'CookiePolicy');
const SignUp = lazyNamed(() => import('./pages/SignUp'), 'SignUp');
const Profile = lazyNamed(() => import('./pages/Profile'), 'Profile');
const SelectRole = lazyNamed(() => import('./pages/SelectRole'), 'SelectRole');
const AdminUsers = lazyNamed(() => import('./pages/Admin/Users'), 'Users');
const AdminCompanies = lazyNamed(() => import('./pages/Admin/Companies'), 'Companies');
const AdminCms = lazyNamed(() => import('./pages/Admin/Cms'), 'CmsPageEditor');
const AdminSettings = lazyNamed(() => import('./pages/Admin/Settings'), 'Settings');
const AdminPromptTemplates = lazyNamed(() => import('./pages/Admin/PromptTemplates'), 'PromptTemplates');
const AdminDataDispositionRequests = lazyNamed(() => import('./pages/Admin/DataDispositionRequests'), 'DataDispositionRequests');
const AdminConsents = lazyNamed(() => import('./pages/Admin/Consents'), 'Consents');
const AdminDataSubjectRequests = lazyNamed(() => import('./pages/Admin/DataSubjectRequests'), 'DataSubjectRequests');
const AdminDevelopmentAreas = lazyNamed(() => import('./pages/Admin/DevelopmentAreas'), 'DevelopmentAreas');
const AdminReports = lazyNamed(() => import('./pages/Admin/Reports'), 'Reports');
const AdminAuditLogs = lazyNamed(() => import('./pages/Admin/AuditLogs'), 'AuditLogs');
const AdminManual = lazyNamed(() => import('./pages/Admin/Manual'), 'AdminManual');
const TrainerParticipants = lazyNamed(() => import('./pages/Trainer/Participants'), 'Participants');
const ParticipantAssignments = lazyNamed(() => import('./pages/Trainer/Participants'), 'ParticipantAssignments');
const TrainerPlans = lazy(() =>
  import('./pages/Trainer/Plans').then((m) => {
    markRouteChunkLoaded();
    return m;
  }),
);
const TrainerPlanDetails = lazyNamed(() => import('./pages/Trainer/PlanDetails'), 'PlanDetails');
const TrainerReviews = lazyNamed(() => import('./pages/Trainer/Reviews'), 'Reviews');
const TrainerProgress = lazyNamed(() => import('./pages/Trainer/Progress'), 'Progress');
const TrainerPlanProposals = lazyNamed(() => import('./pages/Trainer/PlanProposals'), 'PlanProposals');
const ParticipantActionPlan = lazyNamed(() => import('./pages/Participant/ActionPlan'), 'ActionPlanPage');
const ParticipantDashboard = lazyNamed(() => import('./pages/Participant/Dashboard'), 'Dashboard');
const ParticipantSummary = lazyNamed(() => import('./pages/Participant/Summary'), 'ParticipantSummary');
const ParticipantAreaDetail = lazyNamed(() => import('./pages/Participant/Summary'), 'ParticipantAreaDetail');
const ParticipantPlans = lazyNamed(() => import('./pages/Participant/Plans'), 'ParticipantPlans');
const ParticipantPlanDetails = lazyNamed(() => import('./pages/Participant/PlanDetails'), 'ParticipantPlanDetails');
const ParticipantAssignmentActivity = lazyNamed(() => import('./pages/Participant/AssignmentActivity'), 'ParticipantAssignmentActivity');
const ParticipantProgress = lazyNamed(() => import('./pages/Participant/Progress'), 'Progress');
const MentorDashboard = lazyNamed(() => import('./pages/Mentor/Dashboard'), 'MentorDashboard');
const ManagerDashboard = lazyNamed(() => import('./pages/Manager/Dashboard'), 'ManagerDashboard');
const ManagerTeam = lazyNamed(() => import('./pages/Manager/Team'), 'ManagerTeam');
const ManagerReports = lazyNamed(() => import('./pages/Manager/Reports'), 'ManagerReports');
const CompanySummary = lazyNamed(() => import('./pages/CompanyAdmin/Summary'), 'CompanySummary');
const CompanyEmployees = lazyNamed(() => import('./pages/CompanyAdmin/Employees'), 'CompanyEmployees');
const CompanyEnrolments = lazyNamed(() => import('./pages/CompanyAdmin/Enrolments'), 'CompanyEnrolments');
const AdminProgrammes = lazyNamed(() => import('./pages/Admin/Programmes'), 'Programmes');
const AdminQuestionnaires = lazyNamed(() => import('./pages/Admin/Questionnaires'), 'Questionnaires');
const TrainerSessions = lazyNamed(() => import('./pages/Trainer/Sessions'), 'TrainerSessions');
const CalendlyCallback = lazyNamed(() => import('./pages/Trainer/Sessions'), 'CalendlyCallback');
const SessionDetail = lazyNamed(() => import('./pages/Trainer/Sessions'), 'SessionDetail');
const MySessions = lazyNamed(() => import('./pages/Participant/Sessions'), 'MySessions');
const ParticipantSessionDetail = lazyNamed(() => import('./pages/Participant/Sessions'), 'ParticipantSessionDetail');
const MyAssignments = lazyNamed(() => import('./pages/Participant/Assignments'), 'MyAssignments');
const MyAssessments = lazyNamed(() => import('./pages/Participant/Assessments'), 'MyAssessments');
const FillQuestionnaire = lazyNamed(() => import('./pages/Participant/Assessments'), 'FillQuestionnaire');
const EvolutionChart = lazyNamed(() => import('./pages/Participant/Assessments'), 'EvolutionChart');
const ContentLibrary = lazyNamed(() => import('./pages/Content'), 'ContentLibrary');
const ContentDetail = lazyNamed(() => import('./pages/Content'), 'ContentDetail');
const CourseCatalogue = lazyNamed(() => import('./pages/Content'), 'CourseCatalogue');
const CourseDetail = lazyNamed(() => import('./pages/Content'), 'CourseDetail');
const CourseLearn = lazyNamed(() => import('./pages/Content'), 'CourseLearn');
const NotificationsPage = lazyNamed(() => import('./pages/Notifications'), 'NotificationsPage');
const NotificationPreferences = lazyNamed(() => import('./pages/Notifications'), 'NotificationPreferences');
const CheckInForm = lazyNamed(() => import('./pages/Monitoring'), 'CheckInForm');
const CheckInHistory = lazyNamed(() => import('./pages/Monitoring'), 'CheckInHistory');
const ReportsPage = lazyNamed(() => import('./pages/Monitoring'), 'ReportsPage');
const AiAssistant = lazyNamed(() => import('./pages/AI'), 'AiAssistant');
const TrainerInsights = lazyNamed(() => import('./pages/AI'), 'TrainerInsights');
const PaymentAdmin = lazyNamed(() => import('./pages/Payment'), 'PaymentAdmin');
const PrivacyCenter = lazyNamed(() => import('./pages/GDPR'), 'PrivacyCenter');
const ConfirmDeletion = lazyNamed(() => import('./pages/GDPR'), 'ConfirmDeletion');
const FaqPage = lazyNamed(() => import('./pages/FAQ'), 'FaqPage');
const SupportForm = lazyNamed(() => import('./pages/Support'), 'SupportForm');
const Assessment360Admin = lazyNamed(() => import('./pages/Assessment360/Assessment360Admin'), 'Assessment360Admin');
const Assessment360Form = lazyNamed(() => import('./pages/Assessment360/Assessment360Form'), 'Assessment360Form');
const ForgotPassword = lazyNamed(() => import('./pages/ForgotPassword'), 'ForgotPassword');
const ResetPassword = lazyNamed(() => import('./pages/ResetPassword'), 'ResetPassword');
const SetupPassword = lazyNamed(() => import('./pages/SetupPassword'), 'SetupPassword');
const ChangePassword = lazyNamed(() => import('./pages/ChangePassword'), 'ChangePassword');
// Mandatory consent gate (authenticated; pending Terms/Privacy consents)
const RequiredConsent = lazyNamed(() => import('./pages/Consent'), 'RequiredConsent');
const BackofficeDashboard = lazyNamed(() => import('./pages/Backoffice/Dashboard'), 'BackofficeDashboard');

/**
 * Root Application Component
 * 
 * This component provides the core application setup:
 * 
 * 1. **QueryClientProvider**: Configures React Query for server state management
 * 2. **BrowserRouter**: Enables client-side routing
 * 3. **AuthProvider**: Resolves the authenticated user
 * 4. **ThemeProvider**: Applies the platform Material UI theme
 * 
 * All application features are wrapped in these providers to ensure:
 * - Consistent styling across the application
 * - Proper routing functionality
 * - Efficient data fetching and caching
 * - Internationalization support
 */

// Configure React Query client with sensible defaults
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Stale time: 5 minutes (data considered fresh for this duration)
      staleTime: 5 * 60 * 1000,
      // Cache time: 10 minutes (cached data retained for this duration)
      gcTime: 10 * 60 * 1000,
      // Retry failed requests once
      retry: 1,
      // Refetch on window focus in production only
      refetchOnWindowFocus: import.meta.env.PROD,
    },
  },
});

/** Shown while a route chunk is being fetched. */
const RouteFallback: FC = () => {
  const { t } = useTranslation('common');
  return (
    <Box sx={{ display: 'flex', flexGrow: 1, alignItems: 'center', justifyContent: 'center', py: 8 }}>
      <CircularProgress aria-label={t('a11y.loading', 'Loading')} />
    </Box>
  );
};

/**
 * The signed-in navigation (sidebar, role menus, notification bell) is a dozen requests of MUI and
 * icons. It is requested only once the page's own chunk has arrived and its data requests have
 * settled, so on a slow connection it never delays the content the user came for. Routes that are
 * not lazy never report a chunk, hence the timeout.
 */
const NAVIGATION_FALLBACK_DELAY_MS = 4000;
const NAVIGATION_SETTLE_DELAY_MS = 150;

const DeferredNavigation: FC = () => {
  const routeChunkLoaded = useRouteChunkLoaded();
  const fetching = useIsFetching();
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (ready || !routeChunkLoaded || fetching > 0) return undefined;
    const timer = setTimeout(() => setReady(true), NAVIGATION_SETTLE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [ready, routeChunkLoaded, fetching]);

  useEffect(() => {
    const timer = setTimeout(() => setReady(true), NAVIGATION_FALLBACK_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);

  return <Navigation chromeReady={ready} />;
};

const AppFooter: FC = () => {
  const { t } = useTranslation();
  return (
  <Box
    component="footer"
    sx={{
      py: 4,
      px: 2,
      mt: 'auto',
      backgroundColor: 'background.paper',
      borderTop: 1,
      borderColor: 'divider',
      textAlign: 'center',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 1.5,
    }}
  >
    <BrandLogo variant="endorsed" height={60} />
    <Typography variant="body2" color="text.secondary">
      {t('legalLinks.copyright', { year: new Date().getFullYear() })}
    </Typography>
    <Stack direction="row" spacing={2} flexWrap="wrap" justifyContent="center" useFlexGap>
      <Link component={RouterLink} to="/privacy-policy" variant="body2">
        {t('legalLinks.privacy')}
      </Link>
      <Link component={RouterLink} to="/cookies" variant="body2">
        {t('legalLinks.cookies')}
      </Link>
      <Link component="button" type="button" variant="body2" onClick={openCookieSettings}>
        {t('legalLinks.cookieSettings')}
      </Link>
    </Stack>
  </Box>
  );
};

/** Main content area; chromeless pages (e.g. login) get no app-bar offset and no footer. */
const MainArea: FC<{ children: ReactNode }> = ({ children }) => {
  const { pathname } = useLocation();
  const chromeless = isChromelessPath(pathname);
  const showFooter = !chromeless && !hasOwnFooter(pathname);
  return (
    <Box
      component="main"
      sx={{
        flexGrow: 1,
        // Without this the flex item grows to its content's min-content width, so a wide table
        // overflows the whole page instead of scrolling inside its TableContainer.
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        pt: chromeless ? 0 : '64px', // Add padding-top to account for fixed AppBar (standard Toolbar height)
      }}
    >
      {children}
      {/* Always reachable, also on the chromeless pages (login, consent) and the landing page */}
      <Box sx={{ position: 'fixed', bottom: 16, left: 16, zIndex: (theme) => theme.zIndex.tooltip - 1, bgcolor: 'background.paper', borderRadius: 1 }}>
        <LanguageSwitcher />
      </Box>
      {showFooter && <AppFooter />}
      {/* Mandatory PRR funding bar: shown on every page, including chromeless ones and the landing page */}
      <FundingBar sx={showFooter ? undefined : { mt: 'auto' }} />
    </Box>
  );
};

const App: FC = () => {
  return (
    <ErrorBoundary fallback={<ErrorFallback />}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
            <ThemeProvider theme={theme}>
              <CssBaseline />
              <Box sx={{ display: 'flex', minHeight: '100vh' }}>
                {/* Navigation Header (includes sidebar for authenticated users) */}
                <DeferredNavigation />

                {/* Main Content Area */}
                <MainArea>
                  <Suspense fallback={<RouteFallback />}>
                  <Routes>
                    {/* Public Routes */}
                    <Route path="/" element={<Landing />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/terms" element={<Terms />} />
                    <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                    <Route path="/cookies" element={<CookiePolicy />} />
                    <Route path="/login" element={<Login />} />
                    {/* Public self-registration — gated by VITE_FEATURE_SIGNUP.
                        When disabled, /signup is not mounted; users are created via backoffice only. */}
                    {isSignupEnabled && <Route path="/signup" element={<SignUp />} />}
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/setup-password" element={<SetupPassword />} />
                    <Route path="/select-role" element={<SelectRole />} />
                    {/* Public: opened from the emailed GDPR deletion confirmation link, the token itself is the authenticator */}
                    <Route path="/gdpr/confirm-deletion" element={<ConfirmDeletion />} />
                    <Route path="/unauthorized" element={<Unauthorized />} />
                    <Route
                      path="/change-password"
                      element={
                        <ProtectedRoute>
                          <ChangePassword />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/consent"
                      element={
                        <ProtectedRoute>
                          <RequiredConsent />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/profile"
                      element={
                        <ProtectedRoute>
                          <Profile />
                        </ProtectedRoute>
                      }
                    />

                    {/* Legacy Dashboard Route - Redirect to Participant Dashboard */}
                    <Route
                      path="/dashboard"
                      element={
                        <ProtectedRoute>
                          <Navigate to="/participant/dashboard" replace />
                        </ProtectedRoute>
                      }
                    />

                    {/* Admin Role-Protected Routes */}
                    <Route
                      path="/admin/users"
                      element={
                        <RoleProtectedRoute requiredRoles={['ROLE_ADMIN']}>
                          <AdminUsers />
                        </RoleProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/companies"
                      element={
                        <RoleProtectedRoute requiredRoles={['ROLE_ADMIN']}>
                          <AdminCompanies />
                        </RoleProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/cms"
                      element={
                        <RoleProtectedRoute requiredRoles={['ROLE_ADMIN']}>
                          <AdminCms />
                        </RoleProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/prompt-templates"
                      element={
                        <RoleProtectedRoute requiredRoles={['ROLE_ADMIN']}>
                          <AdminPromptTemplates />
                        </RoleProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/data-disposition-requests"
                      element={
                        <RoleProtectedRoute requiredRoles={['ROLE_ADMIN']}>
                          <AdminDataDispositionRequests />
                        </RoleProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/consents"
                      element={
                        <RoleProtectedRoute requiredRoles={['ROLE_ADMIN']}>
                          <AdminConsents />
                        </RoleProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/data-subject-requests"
                      element={
                        <RoleProtectedRoute requiredRoles={['ROLE_ADMIN']}>
                          <AdminDataSubjectRequests />
                        </RoleProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/development-areas"
                      element={
                        <RoleProtectedRoute requiredRoles={['ROLE_ADMIN']}>
                          <AdminDevelopmentAreas />
                        </RoleProtectedRoute>
                      }
                    />
                    <Route
                      path="/backoffice/users"
                      element={
                        <RoleProtectedRoute requiredRoles={['ROLE_ADMIN']}>
                          <AdminUsers />
                        </RoleProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/settings"
                      element={
                        <RoleProtectedRoute requiredRoles={['ROLE_ADMIN']}>
                          <AdminSettings />
                        </RoleProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/reports"
                      element={
                        <RoleProtectedRoute requiredRoles={['ROLE_ADMIN']}>
                          <AdminReports />
                        </RoleProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/audit-logs"
                      element={
                        <RoleProtectedRoute requiredRoles={['ROLE_ADMIN']}>
                          <AdminAuditLogs />
                        </RoleProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/manual"
                      element={
                        <RoleProtectedRoute requiredRoles={['ROLE_ADMIN']}>
                          <AdminManual />
                        </RoleProtectedRoute>
                      }
                    />

                    <Route
                      path="/admin/programmes"
                      element={
                        <RoleProtectedRoute requiredRoles={['ROLE_ADMIN']}>
                          <AdminProgrammes />
                        </RoleProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/questionnaires"
                      element={
                        <RoleProtectedRoute requiredRoles={['ROLE_ADMIN', 'ROLE_TRAINER']}>
                          <AdminQuestionnaires />
                        </RoleProtectedRoute>
                      }
                    />

                    {/* Trainer Role-Protected Routes */}
                    <Route
                      path="/trainer/participants"
                      element={
                        <RoleProtectedRoute requiredRoles={['ROLE_TRAINER']}>
                          <TrainerParticipants />
                        </RoleProtectedRoute>
                      }
                    />
                    <Route
                      path="/trainer/participants/:participantId/assignments"
                      element={
                        <RoleProtectedRoute requiredRoles={['ROLE_TRAINER']}>
                          <ParticipantAssignments />
                        </RoleProtectedRoute>
                      }
                    />
                    <Route
                      path="/trainer/plans"
                      element={
                        <RoleProtectedRoute requiredRoles={['ROLE_TRAINER']}>
                          <TrainerPlans />
                        </RoleProtectedRoute>
                      }
                    />
                    <Route
                      path="/trainer/plans/:id"
                      element={
                        <RoleProtectedRoute requiredRoles={['ROLE_TRAINER']}>
                          <TrainerPlanDetails />
                        </RoleProtectedRoute>
                      }
                    />
                    <Route
                      path="/trainer/plan-proposals"
                      element={
                        <RoleProtectedRoute requiredRoles={['ROLE_TRAINER', 'ROLE_ADMIN']}>
                          <TrainerPlanProposals />
                        </RoleProtectedRoute>
                      }
                    />
                    <Route
                      path="/trainer/reviews"
                      element={
                        <RoleProtectedRoute requiredRoles={['ROLE_TRAINER', 'ROLE_ADMIN']}>
                          <TrainerReviews />
                        </RoleProtectedRoute>
                      }
                    />
                    <Route
                      path="/trainer/progress"
                      element={
                        <RoleProtectedRoute requiredRoles={['ROLE_TRAINER']}>
                          <TrainerProgress />
                        </RoleProtectedRoute>
                      }
                    />
                    <Route
                      path="/trainer/sessions"
                      element={
                        <RoleProtectedRoute requiredRoles={['ROLE_ADMIN', 'ROLE_TRAINER']}>
                          <TrainerSessions />
                        </RoleProtectedRoute>
                      }
                    />
                    <Route
                      path="/trainer/integrations/calendly/callback"
                      element={
                        <RoleProtectedRoute requiredRoles={['ROLE_TRAINER']}>
                          <CalendlyCallback />
                        </RoleProtectedRoute>
                      }
                    />
                    <Route
                      path="/trainer/sessions/:sessionId"
                      element={
                        <RoleProtectedRoute requiredRoles={['ROLE_ADMIN', 'ROLE_TRAINER']}>
                          <SessionDetail />
                        </RoleProtectedRoute>
                      }
                    />

                    {/* Participant Role-Protected Routes */}
                    <Route
                      path="/participant/dashboard"
                      element={
                        <RoleProtectedRoute requiredRoles={['ROLE_PARTICIPANT']}>
                          <ParticipantDashboard />
                        </RoleProtectedRoute>
                      }
                    />
                    <Route
                      path="/participant/summary"
                      element={
                        <RoleProtectedRoute requiredRoles={['ROLE_PARTICIPANT']}>
                          <ParticipantSummary />
                        </RoleProtectedRoute>
                      }
                    />
                    <Route
                      path="/participant/action-plan"
                      element={
                        <RoleProtectedRoute requiredRoles={['ROLE_PARTICIPANT']}>
                          <ParticipantActionPlan />
                        </RoleProtectedRoute>
                      }
                    />
                    <Route
                      path="/participant/summary/areas/:areaKey"
                      element={
                        <RoleProtectedRoute requiredRoles={['ROLE_PARTICIPANT']}>
                          <ParticipantAreaDetail />
                        </RoleProtectedRoute>
                      }
                    />
                    <Route
                      path="/participant/plans"
                      element={
                        <RoleProtectedRoute requiredRoles={['ROLE_PARTICIPANT']}>
                          <ParticipantPlans />
                        </RoleProtectedRoute>
                      }
                    />
                    <Route
                      path="/participant/plans/:id"
                      element={
                        <RoleProtectedRoute requiredRoles={['ROLE_PARTICIPANT']}>
                          <ParticipantPlanDetails />
                        </RoleProtectedRoute>
                      }
                    />
                    <Route
                      path="/participant/plans/:id/assignments/:progressId"
                      element={
                        <RoleProtectedRoute requiredRoles={['ROLE_PARTICIPANT']}>
                          <ParticipantAssignmentActivity />
                        </RoleProtectedRoute>
                      }
                    />
                    <Route
                      path="/participant/progress"
                      element={
                        <RoleProtectedRoute requiredRoles={['ROLE_PARTICIPANT']}>
                          <ParticipantProgress />
                        </RoleProtectedRoute>
                      }
                    />
                    <Route
                      path="/participant/assignments"
                      element={
                        <RoleProtectedRoute requiredRoles={['ROLE_PARTICIPANT']}>
                          <MyAssignments />
                        </RoleProtectedRoute>
                      }
                    />
                    <Route
                      path="/participant/assessments"
                      element={
                        <RoleProtectedRoute requiredRoles={['ROLE_PARTICIPANT']}>
                          <MyAssessments />
                        </RoleProtectedRoute>
                      }
                    />
                    <Route
                      path="/participant/assessments/:assignmentId/fill"
                      element={
                        <RoleProtectedRoute requiredRoles={['ROLE_PARTICIPANT']}>
                          <FillQuestionnaire />
                        </RoleProtectedRoute>
                      }
                    />
                    <Route
                      path="/participant/assessments/evolution"
                      element={
                        <RoleProtectedRoute requiredRoles={['ROLE_PARTICIPANT', 'ROLE_ADMIN']}>
                          <EvolutionChart />
                        </RoleProtectedRoute>
                      }
                    />
                    <Route
                      path="/participant/sessions"
                      element={
                        <RoleProtectedRoute requiredRoles={['ROLE_PARTICIPANT']}>
                          <MySessions />
                        </RoleProtectedRoute>
                      }
                    />
                    <Route
                      path="/participant/sessions/:sessionId"
                      element={
                        <RoleProtectedRoute requiredRoles={['ROLE_PARTICIPANT']}>
                          <ParticipantSessionDetail />
                        </RoleProtectedRoute>
                      }
                    />

                    {/* Mentor Role-Protected Routes */}
                    <Route
                      path="/mentor/dashboard"
                      element={
                        <RoleProtectedRoute requiredRoles={['ROLE_MENTOR']}>
                          <MentorDashboard />
                        </RoleProtectedRoute>
                      }
                    />

                    {/* Manager Role-Protected Routes */}
                    <Route
                      path="/manager/dashboard"
                      element={
                        <RoleProtectedRoute requiredRoles={['ROLE_MANAGER']}>
                          <ManagerDashboard />
                        </RoleProtectedRoute>
                      }
                    />
                    <Route
                      path="/manager/team"
                      element={
                        <RoleProtectedRoute requiredRoles={['ROLE_MANAGER']}>
                          <ManagerTeam />
                        </RoleProtectedRoute>
                      }
                    />
                    <Route
                      path="/manager/reports"
                      element={
                        <RoleProtectedRoute requiredRoles={['ROLE_MANAGER']}>
                          <ManagerReports />
                        </RoleProtectedRoute>
                      }
                    />

                    {/* Company Admin Role-Protected Routes */}
                    <Route
                      path="/company/dashboard"
                      element={
                        <RoleProtectedRoute requiredRoles={['ROLE_COMPANY_ADMIN']}>
                          <CompanySummary />
                        </RoleProtectedRoute>
                      }
                    />
                    <Route path="/company/summary" element={<Navigate to="/company/dashboard" replace />} />
                    <Route
                      path="/company/employees"
                      element={
                        <RoleProtectedRoute requiredRoles={['ROLE_COMPANY_ADMIN']}>
                          <CompanyEmployees />
                        </RoleProtectedRoute>
                      }
                    />
                    <Route
                      path="/company/enrolments"
                      element={
                        <RoleProtectedRoute requiredRoles={['ROLE_COMPANY_ADMIN']}>
                          <CompanyEnrolments />
                        </RoleProtectedRoute>
                      }
                    />
                    {/* Content Library & Courses (all authenticated) */}
                    <Route
                      path="/content"
                      element={
                        <RoleProtectedRoute requiredRoles={['ROLE_ADMIN', 'ROLE_TRAINER', 'ROLE_PARTICIPANT', 'ROLE_MENTOR', 'ROLE_MANAGER', 'ROLE_COMPANY_ADMIN']}>
                          <ContentLibrary />
                        </RoleProtectedRoute>
                      }
                    />
                    <Route
                      path="/content/:contentId"
                      element={
                        <RoleProtectedRoute requiredRoles={['ROLE_ADMIN', 'ROLE_TRAINER', 'ROLE_PARTICIPANT', 'ROLE_MENTOR', 'ROLE_MANAGER', 'ROLE_COMPANY_ADMIN']}>
                          <ContentDetail />
                        </RoleProtectedRoute>
                      }
                    />
                    <Route
                      path="/courses"
                      element={
                        <RoleProtectedRoute requiredRoles={['ROLE_ADMIN', 'ROLE_TRAINER', 'ROLE_PARTICIPANT', 'ROLE_MENTOR', 'ROLE_MANAGER', 'ROLE_COMPANY_ADMIN']}>
                          <CourseCatalogue />
                        </RoleProtectedRoute>
                      }
                    />
                    <Route
                      path="/courses/:courseId"
                      element={
                        <RoleProtectedRoute requiredRoles={['ROLE_ADMIN', 'ROLE_TRAINER', 'ROLE_PARTICIPANT', 'ROLE_MENTOR', 'ROLE_MANAGER', 'ROLE_COMPANY_ADMIN']}>
                          <CourseDetail />
                        </RoleProtectedRoute>
                      }
                    />
                    <Route
                      path="/courses/:courseId/learn"
                      element={
                        <RoleProtectedRoute requiredRoles={['ROLE_ADMIN', 'ROLE_TRAINER', 'ROLE_PARTICIPANT', 'ROLE_MENTOR', 'ROLE_MANAGER', 'ROLE_COMPANY_ADMIN']}>
                          <CourseLearn />
                        </RoleProtectedRoute>
                      }
                    />

                    {/* Notification Routes (all authenticated) */}
                    <Route
                      path="/notifications"
                      element={
                        <ProtectedRoute>
                          <NotificationsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/notifications/preferences"
                      element={
                        <ProtectedRoute>
                          <NotificationPreferences />
                        </ProtectedRoute>
                      }
                    />

                    {/* Monitoring: Check-ins (all authenticated) */}
                    <Route
                      path="/check-in"
                      element={
                        <ProtectedRoute>
                          <CheckInForm />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/check-in/history"
                      element={
                        <ProtectedRoute>
                          <CheckInHistory />
                        </ProtectedRoute>
                      }
                    />

                    {/* Monitoring: Reports (admin, trainer, manager, company admin) */}
                    <Route
                      path="/reports"
                      element={
                        <RoleProtectedRoute requiredRoles={['ROLE_ADMIN', 'ROLE_TRAINER', 'ROLE_MANAGER', 'ROLE_COMPANY_ADMIN']}>
                          <ReportsPage />
                        </RoleProtectedRoute>
                      }
                    />

                    {/* AI Assistant (§4 — available to trainers/admin for summarize & suggest, all for recommendations) */}
                    <Route
                      path="/ai-assistant"
                      element={
                        <RoleProtectedRoute requiredRoles={['ROLE_ADMIN', 'ROLE_TRAINER', 'ROLE_PARTICIPANT', 'ROLE_MENTOR', 'ROLE_MANAGER', 'ROLE_COMPANY_ADMIN']}>
                          <AiAssistant />
                        </RoleProtectedRoute>
                      }
                    />

                    {/* AI Trainer Insights — trainer/admin only, scoped to associated trainees */}
                    <Route
                      path="/ai-assistant/trainer-insights"
                      element={
                        <RoleProtectedRoute requiredRoles={['ROLE_ADMIN', 'ROLE_TRAINER']}>
                          <TrainerInsights />
                        </RoleProtectedRoute>
                      }
                    />

                    {/* Phase K: Payments (admin only) */}
                    <Route
                      path="/admin/payments"
                      element={
                        <RoleProtectedRoute requiredRoles={['ROLE_ADMIN']}>
                          <PaymentAdmin />
                        </RoleProtectedRoute>
                      }
                    />

                    {/* Phase K: GDPR Privacy Center (all authenticated) */}
                    <Route
                      path="/privacy"
                      element={
                        <ProtectedRoute>
                          <PrivacyCenter />
                        </ProtectedRoute>
                      }
                    />

                    {/* Phase K: FAQ (public — also accessible when authenticated) */}
                    <Route path="/faq" element={<FaqPage />} />

                    {/* Phase K: Support (public form available, also authenticated) */}
                    <Route path="/support" element={<SupportForm />} />

                    {/* Phase L: 360 Assessment admin (trainer/admin) */}
                    <Route
                      path="/360"
                      element={
                        <RoleProtectedRoute requiredRoles={['ROLE_ADMIN', 'ROLE_TRAINER', 'ROLE_COMPANY_ADMIN', 'ROLE_MANAGER']}>
                          <Assessment360Admin />
                        </RoleProtectedRoute>
                      }
                    />

                    {/* Phase L: Anonymous 360 response form (public) */}
                    <Route path="/360/respond/:token" element={<Assessment360Form />} />

                    {/* Admin operations routes */}
                    <Route
                      path="/backoffice/dashboard"
                      element={
                        <RoleProtectedRoute requiredRoles={['ROLE_ADMIN']}>
                          <BackofficeDashboard />
                        </RoleProtectedRoute>
                      }
                    />
                    <Route
                      path="/backoffice/programmes"
                      element={
                        <RoleProtectedRoute requiredRoles={['ROLE_ADMIN']}>
                          <AdminProgrammes />
                        </RoleProtectedRoute>
                      }
                    />
                  </Routes>
                  </Suspense>
                </MainArea>

                {/* Cookie consent: one banner for the whole app, reopened from the footers */}
                <CookieConsentBanner />
              </Box>
            </ThemeProvider>
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;
